import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SERVICES = [
  "Web Development",
  "Web Application",
  "AI Integrations",
  "E-Commerce",
  "SaaS Development",
  "UI/UX Design",
  "Maintenance",
  "Custom Software",
];

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    service_interest: SERVICES[0],
    budget: "",
    preferred_contact: "email",
    message: "",
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.length < 5) {
      toast.error("Please describe your project a bit more.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — CODER will reply within 24 hours.");
      setForm({ ...form, full_name: "", email: "", phone: "", company: "", budget: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send. Please WhatsApp CODER directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-32 lg:py-44 px-6 lg:px-10 border-t border-nova-line">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="lg:col-span-7"
        >
          <div className="mono-label mb-4">[ 07 — Let's build ]</div>
          <h2 className="font-display font-bold tracking-tighter text-4xl lg:text-6xl text-nova-text leading-[0.95] mb-10">
            Start your <span className="gold-text italic font-light">project</span>.
          </h2>

          <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
            <input
              type="text"
              value={form.website}
              onChange={handle("website")}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
            />
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              <div>
                <label className="mono-label block mb-1">Full name *</label>
                <input
                  type="text"
                  required
                  value={form.full_name}
                  onChange={handle("full_name")}
                  placeholder="Jane Doe"
                  className="brutalist-input"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label className="mono-label block mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={handle("email")}
                  placeholder="jane@company.com"
                  className="brutalist-input"
                  data-testid="contact-input-email"
                />
              </div>
              <div>
                <label className="mono-label block mb-1">Phone *</label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={handle("phone")}
                  placeholder="+91 ..."
                  className="brutalist-input"
                  data-testid="contact-input-phone"
                />
              </div>
              <div>
                <label className="mono-label block mb-1">Company</label>
                <input
                  type="text"
                  value={form.company}
                  onChange={handle("company")}
                  placeholder="Optional"
                  className="brutalist-input"
                  data-testid="contact-input-company"
                />
              </div>
              <div>
                <label className="mono-label block mb-1">Service interest</label>
                <select
                  value={form.service_interest}
                  onChange={handle("service_interest")}
                  className="brutalist-input cursor-pointer"
                  data-testid="contact-select-service"
                >
                  {SERVICES.map((s) => (
                    <option key={s} value={s} className="bg-nova-bg">{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mono-label block mb-1">Budget (USD)</label>
                <input
                  type="text"
                  value={form.budget}
                  onChange={handle("budget")}
                  placeholder="e.g. $500"
                  className="brutalist-input"
                  data-testid="contact-input-budget"
                />
              </div>
            </div>

            <div>
              <label className="mono-label block mb-1">Project description *</label>
              <textarea
                required
                rows={4}
                value={form.message}
                onChange={handle("message")}
                placeholder="Tell us about your vision, timeline and goals…"
                className="brutalist-input resize-none"
                data-testid="contact-input-message"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="group inline-flex items-center gap-3 bg-nova-gold text-nova-bg px-8 py-4 font-medium hover:bg-[#C9F9D6] transition-colors disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send Message"}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
          </form>
        </motion.div>

        <motion.aside
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="lg:col-span-5 lg:pl-10 lg:border-l lg:border-nova-line"
        >
          <h3 className="font-display text-2xl text-nova-text mb-2">Or talk to CODER directly.</h3>
          <p className="text-nova-mute mb-10">Replies within 24 hours, often sooner.</p>

          <div className="space-y-4">
            <a href="tel:+916260943446" data-testid="contact-direct-phone" className="flex items-center gap-4 border border-nova-line p-5 hover:border-nova-gold/40 transition-colors">
              <Phone className="w-5 h-5 text-nova-gold" strokeWidth={1.5} />
              <div>
                <div className="mono-label">Phone</div>
                <div className="text-nova-text font-medium">+91 6260943446</div>
              </div>
            </a>
            <a href="mailto:novacreationscodes@gmail.com" data-testid="contact-direct-email" className="flex items-center gap-4 border border-nova-line p-5 hover:border-nova-gold/40 transition-colors">
              <Mail className="w-5 h-5 text-nova-gold" strokeWidth={1.5} />
              <div>
                <div className="mono-label">Email</div>
                <div className="text-nova-text font-medium">novacreationscodes@gmail.com</div>
              </div>
            </a>
            <a href="https://wa.me/916260943446?text=Hi%2C%20I'd%20like%20to%20discuss%20a%20project." target="_blank" rel="noreferrer" data-testid="contact-direct-whatsapp" className="flex items-center gap-4 border border-nova-line p-5 hover:border-nova-gold/40 transition-colors">
              <MessageCircle className="w-5 h-5 text-nova-gold" strokeWidth={1.5} />
              <div>
                <div className="mono-label">WhatsApp</div>
                <div className="text-nova-text font-medium">Open chat →</div>
              </div>
            </a>
          </div>
        </motion.aside>
      </div>
    </section>
  );
}
